import type React from 'react';

import type {
  CellRenderProps,
  DataGridTooltipRenderProps,
  SortType,
} from './data-grid.types';

export type DataGridHeaderProps<T> = {
  name: string;
  cellRender?: (props: CellRenderProps<T>) => React.ReactNode;
  // Compute a plain-text value for the cell. Use this (instead of cellRender)
  // when the cell is text that should truncate with an ellipsis and show a
  // tooltip when clipped — AG-Grid's `whenTruncated` tooltip mode only works on
  // cells WITHOUT a custom cell renderer.
  valueGetter?: (params: { data?: T }) => string | null;
  // Show the cell's value in a tooltip, but only when the text is truncated.
  // Only takes effect on text cells (no `cellRender`); see `valueGetter`.
  tooltip?: boolean;
  // Provide the tooltip text from the row instead of using the displayed cell
  // value. Defaults to the cell value when omitted; only takes effect alongside
  // `tooltip`.
  tooltipValueGetter?: (params: { data?: T }) => string | null;
  /*
    Render this column's tooltip yourself instead of the grid's default, which puts the value
    through a read-only Textarea. Only takes effect alongside `tooltip`.

    The component is handed the row as well as the value, so it can show what the cell does not.
    **It is still gated by the grid's `tooltipShowMode`**, which defaults to `whenTruncated` - a
    tooltip with more in it than the cell wants `tooltipShowMode="standard"` on the `<DataGrid />`,
    or it only appears once the cell's own text is clipped.
  */
  tooltipComponent?: React.ComponentType<DataGridTooltipRenderProps<T>>;
  displayName?: string | (() => React.JSX.Element | null) | null;
  FiltersRender?: () => React.JSX.Element;
  pinned?: 'left' | 'right' | null;
  lockPinned?: boolean;
  /*
    Take this column out of the reordering entirely: no drag starts on its header, so it cannot be
    moved and none of the feedback a move would show appears either.

    **`lockPinned` is not this.** That one settles where a column may be pinned, not whether it may
    be dragged: a pinned column keeps its side and an unpinned one can never be given one, but
    either still moves within its own section, so the drag begins and the grid draws its ghost
    regardless. A column that is furniture rather than data - one whose header holds a control -
    wants this instead. To stop the reader pinning anything at all, `<DataGrid lockColumnPinning />`
    sets that as every column's default.
  */
  suppressMovable?: boolean;
  sortingOrder?: SortType[];
  initialSort?: SortType;
  width?: number;
  stretched?: boolean;
  /*
    Let the cell wrap onto as many lines as its text needs, and size the row to fit the tallest
    cell in it. **Pagination mode only**: row heights are per row there, where the infinite row
    model positions every row off one shared height (`rowTop = rowHeight * index`), so a taller
    row would be drawn over the ones below it.
  */
  wrapText?: boolean;
  /*
    Let the header wrap onto as many lines as its text needs, and grow the header row to fit the
    tallest one. The wrapping on its own is already the default; the height is not, and a header
    row stays one line tall and clips the rest without this.
  */
  wrapHeaderText?: boolean;
  /*
    A class on every cell in this column, for styling a column the design system has no prop for.

    **A class rather than the column's own name.** A cell already carries `col-id="<name>"`, so a
    consumer could select on that today - and then the stylesheet breaks when the field is renamed,
    and reads as ag-grid internals either way. A name given here says what the column is being
    styled *as*.
  */
  cellClassName?: string;
  onCellClicked?: (data: Record<string, unknown>) => void;
};

/** Declares a column; `DataGrid` reads these props and renders nothing of this element itself. */
export const DataGridHeader = <T,>(_props: DataGridHeaderProps<T>) => null;
